import React, { useEffect } from 'react';
import FlightListItem from './FlightListItem';
import { useTranslations } from '@/lib/i18n';
import { useSelector } from 'react-redux';
import { ArrowUp } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Root {
  data: Array<{
    flightInfo: Array<{
      departure_time: string;
      arrival_time: string;
      duration: string;
      departure_city_name: string;
      arrival_city_name: string;
    }>;
    price: number;
    airline_code: string;
    flight_number: string;
  }>;
}

interface FlightListI {
  data: Root;
  type: 'OUT_BOUND' | 'RETURN';
  title: string;
  icon: any;
  nonStopOnly?: boolean;
}

type SortField = 'departure' | 'duration' | 'arrival' | 'price';
type SortOrder = 'asc' | 'desc';

export default function FlightList({
  data,
  type,
  title,
  icon,
  nonStopOnly = false,
}: FlightListI) {
  const t = useTranslations('FlightListPage');
  const filterState = useSelector((state: any) => state.flightFilters);
  const flightSearchInput = useSelector((state: any) => state.flightSearch);
  const filteredData =
    type === 'OUT_BOUND'
      ? filterState.filteredData
      : filterState.filteredReturnFlight;

  const [sortField, setSortField] = React.useState<SortField>('price');
  const [sortOrder, setSortOrder] = React.useState<SortOrder>('asc');

  const sortFlights = (flights: any[]) => {
    if (!flights.length) return [];

    return [...flights].sort((a, b) => {
      const firstFlight = a.flightInfo[0];
      const secondFlight = b.flightInfo[0];

      switch (sortField) {
        case 'departure':
          const departureTimeA = firstFlight.departure_time; // e.g. "10:30"
          const departureTimeB = secondFlight.departure_time;
          return sortOrder === 'asc'
            ? departureTimeA.localeCompare(departureTimeB)
            : departureTimeB.localeCompare(departureTimeA);

        case 'arrival':
          const arrivalTimeA = firstFlight.arrival_time; // e.g. "13:45"
          const arrivalTimeB = secondFlight.arrival_time;
          return sortOrder === 'asc'
            ? arrivalTimeA.localeCompare(arrivalTimeB)
            : arrivalTimeB.localeCompare(arrivalTimeA);

        case 'duration':
          const getDuration = (flight: any) => {
            const duration = flight.duration;
            const hours = parseInt(duration.match(/(\d+)h/)?.[1] || '0');
            const minutes = parseInt(duration.match(/(\d+)m/)?.[1] || '0');
            return hours * 60 + minutes;
          };
          return sortOrder === 'asc'
            ? getDuration(firstFlight) - getDuration(secondFlight)
            : getDuration(secondFlight) - getDuration(firstFlight);

        case 'price':
          const priceA = a.price_details[0]?.fn_totalAmount || 0;
          const priceB = b.price_details[0]?.fn_totalAmount || 0;
          return sortOrder === 'asc' ? priceA - priceB : priceB - priceA;

        default:
          return 0;
      }
    });
  };

  const applyNonStopFilter = (flights: any[] = []) => {
    if (!nonStopOnly) return flights;
    return flights.filter((flight) => {
      const firstSegment = flight?.flightInfo?.[0];
      if (!firstSegment) return false;
      const hasNoStops = Number(firstSegment?.stopCount ?? 0) === 0;
      const stopLabel = String(firstSegment?.stops || '').toLowerCase();
      return hasNoStops || stopLabel.includes('non');
    });
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const useScreenSize = () => {
    const [screenSize, setScreenSize] = React.useState({
      isMobile: false,
      isTablet: false,
      isLaptop: false,
      isDesktop: false,
    });

    React.useEffect(() => {
      const handleResize = () => {
        const width = window.innerWidth;
        setScreenSize({
          isMobile: width < 640,
          isTablet: width >= 640 && width < 1024,
          isLaptop: width >= 1024 && width < 1280,
          isDesktop: width >= 1280,
        });
      };

      // Initial check
      handleResize();

      // Add event listener
      window.addEventListener('resize', handleResize);

      // Cleanup
      return () => window.removeEventListener('resize', handleResize);
    }, []);

    return screenSize;
  };

  const SortButton = ({
    field,
    label,
  }: {
    field: SortField;
    label: string;
  }) => (
    <Button
      variant="ghost"
      onClick={() => handleSort(field)}
      className={`flex h-7 items-center px-2 text-xs font-semibold hover:text-primary hover:shadow-none sm:h-5 sm:flex-row sm:items-center sm:text-sm ${
        sortField === field ? 'text-primary' : ''
      }`}
    >
      <span>{label}</span>
      {sortField === field && (
        <ArrowUp
          className={`ml-0 h-2 w-2 transform text-primary duration-300 sm:ml-1 sm:mt-0 sm:h-3 sm:w-3 ${
            sortOrder === 'desc' ? 'rotate-0' : 'rotate-180'
          }`}
        />
      )}
    </Button>
  );

  return (
    <div
      className={`no-scrollbar mb-4 flex min-w-0 flex-1 flex-col overflow-y-auto ${flightSearchInput?.travelType === 'RT' ? 'pb-16 sm:pb-20 lg:pb-24' : ''} transition-all duration-300`}
    >
      <div className="flex flex-1 flex-col">
        {/* Column Headers for sorting - Desktop only */}
        <div className="flex w-full flex-row justify-between rounded border bg-background px-2 py-2 shadow sm:px-4 md:px-6 lg:px-8">
          <div className="flex basis-[16%] items-center sm:basis-[32%] md:basis-[23%]">
            <h5
              className={`h-5 px-2 text-xs font-semibold hover:text-primary hover:shadow-none`}
            >
              {t('flightListPage.airlines')}
            </h5>
          </div>
          <div className="flex basis-[18%] items-center justify-center sm:basis-[15%] md:basis-[12%]">
            <SortButton
              field="departure"
              label={t('flightListPage.departure')}
            />
          </div>
          <div className="hidden basis-[12%] justify-center sm:flex sm:basis-[16%] md:basis-[12%]">
            <SortButton field="duration" label={t('flightListPage.Duration')} />
          </div>
          <div className="flex basis-[20%] justify-center sm:basis-[18%] md:basis-[12%]">
            <SortButton field="arrival" label={t('flightListPage.Arrival')} />
          </div>
          <div className="flex basis-[20%] justify-center sm:basis-[16%] md:basis-[20%]">
            <SortButton field="price" label={t('flightListPage.Price')} />
          </div>
          <div className="hidden basis-[10%] justify-start sm:flex"></div>
        </div>
        {(filterState?.filteredData?.length > 0 ||
          (flightSearchInput?.travelType === 'RT' &&
            flightSearchInput?.travelMode === 'DO' &&
            filterState?.filteredReturnFlight?.length > 0)) && (
          <div className="flex sm:block">
            <div
              className={`flex w-full transition-all duration-300 ease-in-out`}
            >
              <div className={`w-[100%] transition-all duration-300 sm:w-full`}>
                <ListFlight
                  data={
                    type === 'OUT_BOUND'
                      ? sortFlights(applyNonStopFilter(filteredData))
                      : sessionStorage.getItem('returnFilter')
                        ? sortFlights(
                            applyNonStopFilter(filterState?.filteredReturnFlight),
                          )
                        : sortFlights(
                            applyNonStopFilter(filterState?.returnData),
                          )
                  }
                  type={type}
                  title={title}
                  icon={icon}
                />
              </div>

              {/* <div
                className={`w-[10%] overflow-hidden opacity-50 transition-all duration-300 sm:hidden`}
              >
                <div className="flex h-full w-full items-center justify-center">
                  <div className="rotate-90 whitespace-nowrap text-xs text-gray-500">
                    {type === 'OUT_BOUND' ? 'Return' : 'Outbound'}
                  </div>
                </div>
              </div> */}
            </div>
          </div>
        )}
        {filterState?.filteredData?.length == 0 &&
          (flightSearchInput?.travelType == 'OW' ||
            flightSearchInput?.travelType == 'RT') && (
            <div className="flex flex-col items-center justify-center py-8 text-gray-500">
              <p className="text-sm sm:text-base">No flights found </p>
            </div>
          )}
      </div>
    </div>
  );
}

const ListFlight = ({
  data,
  type,
  title,
  icon,
}: {
  data: any;
  type: string;
  title: string;
  icon: any;
}) => {
  const flightSearchInput = useSelector((state: any) => state.flightSearch);
  return (
    <div className="flex flex-col">
      {flightSearchInput?.travelType === 'RT' &&
        flightSearchInput?.travelMode === 'DO' && (
          <div className="sticky top-0 bg-gray-50/80 backdrop-blur-sm">
            {/* Mobile View Title */}
            <h4 className="flex hidden items-center gap-2 rounded-lg bg-white p-2.5 text-sm font-medium shadow-sm">
              <span className="h-4 w-4 flex-shrink-0">{icon}</span>
              <span className="line-clamp-1 flex-1">{title}</span>
            </h4>

            {/* Tablet View Title */}
            <h4 className="hidden items-center gap-3 rounded-lg bg-white p-3 text-base font-semibold shadow-sm sm:flex lg:hidden">
              <span className="h-5 w-5 flex-shrink-0">{icon}</span>
              <span className="line-clamp-1">{title}</span>
            </h4>

            {/* Desktop View Title */}
            <h4 className="hidden items-center gap-3 rounded-lg bg-white p-4 text-sm font-semibold shadow-sm lg:flex">
              <span className="h-6 w-6 flex-shrink-0">{icon}</span>
              {title}
            </h4>
          </div>
        )}

      <div className="space-y-2 sm:space-y-3 lg:space-y-4">
        {data?.length > 0 ? (
          data?.map((item: any, index: number) => (
            <div
              key={`flight-${item.airline_code}-${item.flight_number}-${index}`}
              className={`duration-300 animate-in fade-in ${index === 0 ? 'slide-in-from-top-4' : ''} `}
            >
              <FlightListItem data={item} type={type} />
            </div>
          ))
        ) : (
          <div className="flex flex-col items-center justify-center py-8 text-gray-500">
            <p className="text-sm sm:text-base">No flights found</p>
          </div>
        )}
      </div>
    </div>
  );
};
